import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

import { Calendar_onValueChangeEvent } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { FilterDateCalendarComponent } from 'app/components/filter-date-calendar/filter-date-calendar.component';
import { FilterService, OperatorType } from 'app/modules/collection/services/filter.service';
import { DataSourceItem } from 'app/modules/widget/components/data-source-chooser/data-source-chooser.component';
import { Session } from 'app/providers/bridge-api/auth.service';
import {
  Dashboard,
  DashboardService,
  DataSourceFilter,
  DataSourceFilterSet,
  DataSourceFilterType,
} from 'app/providers/bridge-api/dashboard.service';
import {
  ContentChannel,
  ContentChannelService,
} from 'app/providers/storage-api/content-channel.service';
import { ObjectLabel } from 'app/providers/storage-api/object-label.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { DashboardDatasourceFilterService } from '../../services/dashboard-datasource-filter.service';

import { DashboardDatasourceFilterPillComponent } from './dashboard-datasource-filter-pill/dashboard-datasource-filter-pill.component';

@Component({
  selector: 'app-dashboard-datasource-rules-modal',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AppSharedModule, DashboardDatasourceFilterPillComponent, FilterDateCalendarComponent],
  templateUrl: './dashboard-datasource-rules-modal.component.html',
  styleUrl: './dashboard-datasource-rules-modal.component.scss',
})
export class DashboardDatasourceRulesModalComponent implements OnInit {
  @Input() dashboard: Dashboard;
  @Input() dataSources: DataSourceItem[];
  @Input() isReadOnly: boolean;
  @Input() session: Session;
  @Input() projectId: string;
  @Output() modalClosed = new EventEmitter<boolean>();
  private destroyRef = inject(DestroyRef);
  private search$ = new Subject<string>();
  searchValue: string;
  //Checked checkboxes
  selected: DataSourceFilter[] = [];
  displayedSelectedFilters: DataSourceFilterSet = {
    and: [],
    or: [],
    not: [],
  };

  translations: any;

  availableAccordions: {
    title: string;
    id: DataSourceFilterType;
    templateType: string;
    data: DataSourceFilter[];
  }[];

  operators: OperatorType[] = ['and', 'or', 'not'];
  filterCounts: {
    survey: { count: number; dsMatches: string[] };
    postCallSurvey: { count: number; dsMatches: string[] };
    analysis: { count: number; dsMatches: string[] };
    group: { count: number; dsMatches: string[] };
  };
  objectLabels: DataSourceFilter[];
  isLoading: boolean;
  private contentChannelChains = new Map<string, ContentChannel[]>();

  constructor(
    private translocoService: TranslocoService,
    private dashboardService: DashboardService,
    private alertService: AlertService,
    private filterService: FilterService,
    private dashboardDataSourceFilterService: DashboardDatasourceFilterService,
    private dialogService: DialogService,
    private contentChannelService: ContentChannelService
  ) {}

  ngOnInit() {
    this.translations = this.translocoService.translateObject('dashboard-datasource-rules');
    this.availableAccordions = [
      {
        title: this.translations['content-channels'],
        id: 'contentChannel',
        templateType: 'checkbox',
        data: [],
      },
      {
        title: this.translations['data-source-types'],
        id: 'dataSourceType',
        templateType: 'checkbox',
        data: [],
      },
      {
        title: this.translations['date'],
        id: 'date',
        templateType: 'date',
        data: [],
      },
      {
        title: this.translations['specific-data-source'],
        id: 'specificDataSource',
        templateType: 'checkbox',
        data: [],
      },
      {
        title: this.translations['labels'],
        id: 'label',
        templateType: 'checkbox',
        data: [],
      },
    ];

    this.objectLabels = this.setObjectLabels();
    this.filterCounts = this.dashboardDataSourceFilterService.getFilterCounts(
      this.dataSources,
      this.objectLabels
    );

    this.loadAppliedFilters();
    this.initializeAccordionData();
    this.loadContentChannels();

    this.search$
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.search(val);
      });
  }

  private async loadContentChannels() {
    const tree = await this.contentChannelService.getContentChannels('root', -1);
    // Build lightweight indexes from the tree
    const idToNode = new Map<string, ContentChannel>();
    const parentOf = new Map<string, string | null>();
    for (
      const stack: { node: ContentChannel; parentId: string | null }[] = [
        { node: tree, parentId: null },
      ];
      stack.length;

    ) {
      const current = stack.pop();
      if (!current) {
        continue;
      }
      const { node, parentId } = current;
      if (node?.id) {
        idToNode.set(node.id, node);
        parentOf.set(node.id, parentId);
      }
      if (node?.children?.length) {
        for (const child of node.children) {
          stack.push({ node: child, parentId: node.id ?? null });
        }
      }
    }

    const ids = Array.from(
      new Set(this.dataSources.map((ds) => ds.value.contentChannel).filter(Boolean))
    ) as string[];

    this.contentChannelChains.clear();
    for (const id of ids) {
      if (!idToNode.has(id)) {
        continue;
      }
      const chain: ContentChannel[] = [];
      let cursor: string | null = id;
      while (cursor) {
        const node = idToNode.get(cursor);
        if (!node) {
          break;
        }
        chain.unshift(node);
        cursor = parentOf.get(cursor) ?? null;
      }
      if (chain.length) {
        this.contentChannelChains.set(id, chain);
      }
    }
    const accordion = this.availableAccordions?.find((a) => a.id === 'contentChannel');
    if (accordion) {
      accordion.data = this.getDynamicContentChannels();
    }
  }

  private hasAppliedContentChannelFilter(contentChannelId: string) {
    const appliedFilters = [];
    for (const operator of this.operators) {
      for (const filter of this.displayedSelectedFilters[operator]) {
        if (filter.id === contentChannelId) {
          appliedFilters.push(filter);
        }
      }
    }
    return appliedFilters.length > 0;
  }

  private getDynamicContentChannels() {
    const seen = new Set<string>();
    const channels: { id: string; label: string; type: 'contentChannel'; value: string }[] = [];
    for (const ds of this.dataSources) {
      const contentChannel = ds.value.contentChannel;
      if (
        contentChannel &&
        !seen.has(contentChannel) &&
        !this.hasAppliedContentChannelFilter(contentChannel)
      ) {
        seen.add(contentChannel);
        channels.push({
          id: contentChannel,
          label: this.getContentChannelLabel(contentChannel),
          type: 'contentChannel',
          value: contentChannel,
        });
      }
    }
    return channels.sort((a, b) => a.label.localeCompare(b.label));
  }

  private getContentChannelLabel(contentChannel: string): string {
    const language = this.translocoService.getActiveLang();
    const loc = (n: ContentChannel) => (n.properties as any)?.['label_' + language] || n.label;
    const chain = this.contentChannelChains.get(contentChannel);
    if (chain?.length) {
      return chain
        .filter((n) => n.id !== 'root')
        .map(loc)
        .join(' › ');
    }
    return contentChannel.charAt(0).toUpperCase() + contentChannel.slice(1);
  }

  private loadAppliedFilters() {
    const filters = this.dashboard.payload.dataSourceFilters;
    if (!filters) {
      return;
    }
    for (const operator of this.operators) {
      this.displayedSelectedFilters[operator] = [...filters[operator]];
    }
  }

  private initializeAccordionData() {
    for (const acc of this.availableAccordions) {
      switch (acc.id) {
        case 'contentChannel':
          acc.data = this.getDynamicContentChannels();
          break;
        case 'dataSourceType':
          acc.data = [
            {
              id: 'analysis',
              label: this.translations['analysis'],
              type: 'dataSourceType',
              value: 'analysis',
            },
            {
              id: 'group',
              label: this.translations['analysis-group'],
              type: 'dataSourceType',
              value: 'group',
            },
            {
              id: 'keyDriverAnalysis',
              label: this.translations['key-driver-analysis'],
              type: 'dataSourceType',
              value: 'analysis',
            },
          ];
          break;
        case 'date':
          acc.data = [
            {
              id: 'creationDate',
              label: this.translations['creation-date'],
              type: 'date',
              value: '',
              dateKind: 'created',
              displayValue: '',
            },
            {
              id: 'lastModified',
              label: this.translations['date-last-modified'],
              type: 'date',
              value: '',
              dateKind: 'modified',
              displayValue: '',
            },
          ];
          break;
        case 'specificDataSource':
          acc.data = this.dataSources.map((ds) => ({
            id: ds.value.id,
            value: ds.value.id,
            label: ds.display,
            type: 'specificDataSource',
            dataSourceType: ds.value.type,
          }));
          break;
        case 'label':
          acc.data = this.objectLabels;
      }
    }

    const accordionMap = Object.fromEntries(this.availableAccordions.map((a) => [a.id, a]));

    for (const operator of this.operators) {
      for (const filter of this.displayedSelectedFilters[operator]) {
        const accordion = accordionMap[filter.type];
        if (accordion) {
          accordion.data = accordion.data.filter((i) => i.id !== filter.id);
        }
      }
    }
  }

  private setObjectLabels() {
    const seen = new Set<string>();
    return this.dataSources
      .flatMap((ds) =>
        (ds.value.objectLabels as ObjectLabel[])?.map((ol) => ({
          id: ol.id,
          label: ol.label,
          value: ol.id,
          type: 'label' as const,
        }))
      )
      .filter((label) => {
        if (seen.has(label.id)) {
          return false;
        }
        seen.add(label.id);
        return true;
      });
  }

  onSearch(val: string) {
    const filterVal = val ? val.trim() : '';
    this.search$.next(filterVal);
  }

  private search(val: string) {
    this.initializeAccordionData();
    for (const acc of this.availableAccordions) {
      acc.data = acc.data.filter((i) => i.label.toLowerCase().includes(val.toLowerCase()));
    }
  }

  toggleSelected(isChecked: boolean, filter: DataSourceFilter) {
    if (isChecked && filter.value) {
      this.selected.push(filter);
    } else {
      const index = this.selected.findIndex((i) => i.type === filter.type && i.id === filter.id);
      if (index !== -1) {
        this.selected.splice(index, 1);
      }
    }
  }

  add(operator: OperatorType) {
    for (const selected of this.selected) {
      this.displayedSelectedFilters[operator].push(selected);

      const accordion = this.availableAccordions.find((a) => a.id === selected.type);

      const itemIdx = accordion.data.findIndex((i) => i.id === selected.id);
      if (itemIdx !== -1) {
        accordion.data.splice(itemIdx, 1);
      }
    }
    this.selected = [];
  }

  onFilterAction(
    $event: { filterAction: OperatorType | 'remove' },
    operator: OperatorType,
    filter: DataSourceFilter
  ) {
    if ($event.filterAction === 'remove') {
      this.removeFilter(operator, filter);
    } else {
      this.moveFilter(operator, $event.filterAction, filter);
    }
  }

  removeFilter(operator: OperatorType, filter: DataSourceFilter) {
    const accordion = this.availableAccordions.find((a) => a.id === filter.type);
    accordion.data.push(filter);
    accordion.data.sort((a, b) => a.label.localeCompare(b.label));
    const displayedIdx = this.displayedSelectedFilters[operator].findIndex(
      (i) => i.type === filter.type && i.id === filter.id
    );
    this.displayedSelectedFilters[operator].splice(displayedIdx, 1);
  }

  moveFilter(oldOperator: OperatorType, newOperator: OperatorType, filter: DataSourceFilter) {
    const oldIdx = this.displayedSelectedFilters[oldOperator].findIndex(
      (i) => i.type === filter.type && i.id === filter.id
    );
    const item = this.displayedSelectedFilters[oldOperator][oldIdx];
    this.displayedSelectedFilters[newOperator].push(item);
    this.displayedSelectedFilters[oldOperator].splice(oldIdx, 1);
  }

  onClearAppliedFilters(operator: OperatorType) {
    for (const filter of this.displayedSelectedFilters[operator]) {
      const accordion = this.availableAccordions.find((a) => a.id === filter.type);
      accordion.data.push(filter);
      accordion.data.sort((a, b) => a.label.localeCompare(b.label));
    }
    this.displayedSelectedFilters[operator] = [];
  }

  onDateValueChange(
    $event: Calendar_onValueChangeEvent,
    option: Extract<DataSourceFilter, { type: 'date' }>
  ) {
    option.value = $event.value.toString();
    option.displayValue = this.filterService.computeLabelForDateFilter(option.value);
  }

  onSave() {
    const cachedDashboard = this.dashboardService.loadCachedDashboard(
      this.dashboard.id,
      this.projectId,
      this.session.info2.user_id
    );
    if (this.selected.length > 0 || cachedDashboard) {
      this.openConfirmDialog(!!cachedDashboard);
      return;
    }
    this.save();
  }

  private openConfirmDialog(hasCachedConflict = true) {
    this.dialogService
      .showWarningPopup({
        title: this.translations['warning'],
        content:
          this.translations[hasCachedConflict ? 'overwrite-dashboard' : 'available-items-checked'],
        confirmTitle: this.translations['ok'],
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.save(false);
        }
      });
  }

  private async save(checkConflict = true) {
    this.isLoading = true;

    this.dashboard.payload.dataSourceFilters = JSON.parse(
      JSON.stringify(this.displayedSelectedFilters)
    );

    try {
      await this.dashboardService.updateDashboard(this.dashboard, checkConflict);
      this.isLoading = false;
      this.alertService.showToast(this.translations['dashboard-filters-updated']);
    } catch (ex) {
      this.isLoading = false;
      if (ex.status === 409) {
        this.openConfirmDialog(true);
        return;
      }
      this.alertService.showErrorToast(this.translations['dashboard-filters-updated-fail'], ex);
      return;
    }
    this.modalClosed.emit(true);
  }

  onCancel() {
    this.modalClosed.emit(false);
  }
}
